import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, unwrap } from '@/shared/api-client/workspace';
import type { components } from '@/shared/api-client/workspace/schema';
import type { Weekday } from '@/modules/products/passes';

type Schemas = components['schemas'];

/**
 * MVP holders as Operations › Customers reads them (#517, ADR 0065). A holder is a CustomerProfile
 * found by name, phone or a scanned MVP card. Their MVPs at the Venue carry a derived status, and
 * `liveHours` is the sum of the balances of their LIVE MVPs. An EXPIRED MVP keeps its unused hours
 * as `balance`, so the lost hours stay visible.
 */
export type MvpStatus = NonNullable<Schemas['MvpHolderPassResponse']['status']>;

export type MvpCardStatus = NonNullable<Schemas['MvpHolderCardResponse']['status']>;

export interface MvpHolderMatch {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  liveHours: number;
}

export interface MvpHolderPass {
  id: string;
  optionName: string;
  status: MvpStatus;
  hours: number;
  balance: number;
  /** The Venue-local last valid day, `YYYY-MM-DD`. */
  expiresOn: string;
  /** Days from the Venue-local today to `expiresOn`; negative once expired. */
  daysLeft: number;
  /** An empty list = every day. */
  validWeekdays: Weekday[];
  validToday: boolean;
  price: string;
  currency: string;
  soldAt: string;
  saleId: string;
  receiptNumber: string | null;
  soldBy: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  voidedBy: string | null;
  refundedAt: string | null;
  refundReason: string | null;
  refundedBy: string | null;
  refundAmount: { amount: string; currency: string } | null;
  /** How the money went back (ADR 0068); null on refunds recorded before it was captured. */
  refundMethod: RefundMethod | null;
}

export interface MvpHolderCard {
  id: string;
  /** e.g. `•••• 9015` */
  maskedCard: string;
  status: MvpCardStatus;
  boundAt: string;
}

export interface MvpHolder {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  liveHours: number;
  liveCount: number;
  multiVisitPasses: MvpHolderPass[];
  cards: MvpHolderCard[];
}

export interface MvpVisit {
  id: string;
  saleId: string;
  receiptNumber: string | null;
  servedBy: string | null;
  guests: number;
  stayHours: number;
  enteredAt: string;
  playUntil: string;
  undone: boolean;
  draws: {
    multiVisitPassId: string;
    optionName: string;
    hours: number;
    undoneAt: string | null;
    undoReason: string | null;
    undoneBy: string | null;
  }[];
}

export const generateMvpCardNumbers = (venueId: string, count: number): Promise<string[]> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/venues/{venueId}/mvp-cards/generate', {
      params: { path: { venueId } },
      body: { count },
    }),
  ).then(({ numbers }) => numbers ?? []);

export const getMvpVisits = (venueId: string, customerId: string): Promise<MvpVisit[]> =>
  unwrap(
    apiClient.GET('/api/v1/venue-workspace/venues/{venueId}/mvp-holders/{customerId}/visits', {
      params: { path: { venueId, customerId } },
    }),
  ) as Promise<MvpVisit[]>;

export const undoMvpVisit = (
  venueId: string,
  customerId: string,
  visitId: string,
  reason: string,
) =>
  unwrap(
    apiClient.POST(
      '/api/v1/venue-workspace/venues/{venueId}/mvp-holders/{customerId}/visits/{visitId}/undo',
      { params: { path: { venueId, customerId, visitId } }, body: { reason } },
    ),
  );

export function useMvpVisits(venueId: string, customerId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...holdersKey(venueId), 'holder', customerId, 'visits'],
    queryFn: () => getMvpVisits(venueId, customerId),
    enabled,
  });
}

export function useUndoMvpVisit(venueId: string, customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ visitId, reason }: { visitId: string; reason: string }) =>
      undoMvpVisit(venueId, customerId, visitId, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: holdersKey(venueId) }),
  });
}

const holdersKey = (venueId: string) => ['mvp-holders', venueId] as const;

/** The sales PaymentMethod wire codes an MVP refund can be paid back in. */
export type RefundMethod = Schemas['PaymentMethod'];

const passPath =
  '/api/v1/venue-workspace/venues/{venueId}/mvp-holders/{customerId}/passes/{passId}';

export const voidMvp = (venueId: string, customerId: string, passId: string, reason: string) =>
  unwrap(
    apiClient.POST(`${passPath}/void`, {
      params: { path: { venueId, customerId, passId } },
      body: { reason },
    }),
  );

export const refundMvp = (
  venueId: string,
  customerId: string,
  passId: string,
  reason: string,
  amount?: string,
  method?: RefundMethod,
) =>
  unwrap(
    apiClient.POST(`${passPath}/refund`, {
      params: { path: { venueId, customerId, passId } },
      body: { reason, amount, method },
    }),
  );

export function useVoidOrRefundMvp(venueId: string, customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      passId,
      kind,
      reason,
      amount,
      method,
    }: {
      passId: string;
      kind: 'void' | 'refund';
      reason: string;
      amount?: string;
      method?: RefundMethod;
    }) =>
      kind === 'void'
        ? voidMvp(venueId, customerId, passId, reason)
        : refundMvp(venueId, customerId, passId, reason, amount, method),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: holdersKey(venueId) }),
  });
}

export const searchMvpHolders = (venueId: string, query: string): Promise<MvpHolderMatch[]> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/venues/{venueId}/mvp-holders/search', {
      params: { path: { venueId } },
      body: { query },
    }),
  ).then((response) => (response.items ?? []) as MvpHolderMatch[]);

export const getMvpHolder = (venueId: string, customerId: string): Promise<MvpHolder> =>
  unwrap(
    apiClient.GET('/api/v1/venue-workspace/venues/{venueId}/mvp-holders/{customerId}', {
      params: { path: { venueId, customerId } },
    }),
  ) as Promise<MvpHolder>;

export function useMvpHolderSearch(venueId: string, query: string, enabled: boolean) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: [...holdersKey(venueId), 'search', trimmed],
    queryFn: () => searchMvpHolders(venueId, trimmed),
    enabled: enabled && trimmed.length > 0,
  });
}

export function useMvpHolder(venueId: string, customerId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: [...holdersKey(venueId), 'holder', customerId],
    queryFn: () => getMvpHolder(venueId, customerId ?? ''),
    enabled: enabled && customerId !== null,
  });
}
